import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, FileText, Stethoscope, TestTube, Pill, AlertCircle, Activity, Heart, Shield, BookOpen, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getConditionBySlug } from '../data/conditions';
import { ResourceLevel } from '../types';

export default function ConditionDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useLanguage();
  const condition = getConditionBySlug(slug || '');

  if (!condition) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Condition Not Found</h1>
          <Link to="/conditions" className="text-blue-600 hover:underline">Back to Conditions</Link>
        </div>
      </div>
    );
  }

  const getLevelStyle = (level: ResourceLevel) => {
    switch (level) {
      case 'BASIC': return { bg: 'bg-green-500', text: 'Primary Care' };
      case 'INTERMEDIATE': return { bg: 'bg-yellow-500', text: 'District Hospital' };
      case 'ADVANCED': return { bg: 'bg-red-500', text: 'Referral Hospital' };
    }
  };

  const levelStyle = getLevelStyle(condition.resourceLevel);

  // Helper function to render formatted text content (handles ════ sections)
  const renderFormattedText = (text: string) => {
    return (
      <div className="medical-content">
        {text}
      </div>
    );
  };

  return (
    <div className="condition-page-wrapper">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container-responsive py-6 sm:py-8">
          <Link to="/conditions" className="inline-flex items-center text-blue-100 hover:text-white mb-4 text-sm sm:text-base">
            <ArrowLeft className="w-4 h-4 mr-2 flex-shrink-0" /> Back to All Conditions
          </Link>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 break-words">{condition.name}</h1>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-white/20 rounded-full text-xs sm:text-sm">{condition.specialty}</span>
                <span className="px-3 py-1 bg-white/20 rounded-full text-xs sm:text-sm">{condition.bodySystem}</span>
                <span className="px-3 py-1 bg-white/20 rounded-full text-xs sm:text-sm">ICD: {condition.icdCode}</span>
                {condition.isEmergency && (
                  <span className="px-3 py-1 bg-red-500 rounded-full text-xs sm:text-sm font-semibold">🚨 Emergency</span>
                )}
              </div>
            </div>
            <div className={`${levelStyle.bg} px-4 py-2 rounded-lg text-center flex-shrink-0`}>
              <div className="font-bold text-sm sm:text-base">{condition.resourceLevel}</div>
              <div className="text-xs sm:text-sm opacity-90">{levelStyle.text}</div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container-responsive py-6 sm:py-8">
        <div className="condition-grid">
          
          {/* Left Column - Main Content */}
          <div className="condition-main-content space-y-6">
            
            {/* Overview Section */}
            <section className="content-card">
              <h2 className="section-title">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" /> {t('overview')}
              </h2>
              {renderFormattedText(condition.overview)}
            </section>

            {/* Epidemiology Section */}
            {condition.epidemiology && (
              <section className="content-card">
                <h2 className="section-title">
                  <Users className="w-5 h-5 text-blue-600 flex-shrink-0" /> Epidemiology
                </h2>
                {renderFormattedText(condition.epidemiology)}
              </section>
            )}

            {/* Clinical Presentation Section */}
            <section className="content-card">
              <h2 className="section-title">
                <Stethoscope className="w-5 h-5 text-blue-600 flex-shrink-0" /> {t('clinicalPresentation')}
              </h2>
              {renderFormattedText(condition.clinicalPresentation)}
            </section>

            {/* Diagnosis Section */}
            <section className="content-card">
              <h2 className="section-title">
                <TestTube className="w-5 h-5 text-blue-600 flex-shrink-0" /> {t('diagnosis')}
              </h2>
              {renderFormattedText(condition.diagnosis)}
              
              {condition.investigations && condition.investigations.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-800 mb-3">{t('investigations')}</h3>
                  <div className="space-y-2">
                    {condition.investigations.map((inv, i) => (
                      <div key={i} className="investigation-item">
                        <span className={`availability-badge ${
                          inv.availability === 'BASIC' ? 'badge-basic' :
                          inv.availability === 'INTERMEDIATE' ? 'badge-intermediate' :
                          'badge-advanced'
                        }`}>{inv.availability}</span>
                        <div className="investigation-content">
                          <span className="font-medium text-gray-900">{inv.name}</span>
                          <p className="text-gray-500 text-sm mt-1">{inv.indication}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Management Section */}
            <section className="content-card">
              <h2 className="section-title">
                <Pill className="w-5 h-5 text-blue-600 flex-shrink-0" /> {t('management')}
              </h2>
              
              {/* Immediate Actions */}
              {condition.management.immediate && condition.management.immediate.length > 0 && (
                <div className="mb-6">
                  <h3 className="subsection-title text-red-600">
                    <AlertTriangle className="w-4 h-4" /> {t('immediateActions')}
                  </h3>
                  <ol className="list-decimal list-inside space-y-2">
                    {condition.management.immediate.map((action, i) => (
                      <li key={i} className="text-gray-700 text-content">{action}</li>
                    ))}
                  </ol>
                </div>
              )}
              
              {/* Pharmacological */}
              {condition.management.pharmacological && condition.management.pharmacological.length > 0 && (
                <div className="mb-6">
                  <h3 className="subsection-title">{t('pharmacological')}</h3>
                  <div className="space-y-4">
                    {condition.management.pharmacological.map((drug, i) => (
                      <div key={i} className="drug-card">
                        <div className="font-semibold text-blue-700">{drug.drug}</div>
                        <div className="text-sm text-gray-600 mt-1">{drug.indication}</div>
                        <div className="text-sm mt-2 space-y-1">
                          <div><span className="font-medium text-gray-700">Dose:</span> <span className="text-gray-600">{drug.dose}</span></div>
                          <div><span className="font-medium text-gray-700">Route:</span> <span className="text-gray-600">{drug.route}</span></div>
                          {drug.duration && <div><span className="font-medium text-gray-700">Duration:</span> <span className="text-gray-600">{drug.duration}</span></div>}
                          {drug.contraindications && drug.contraindications.length > 0 && (
                            <div className="mt-2">
                              <span className="font-medium text-red-600">Contraindications:</span>
                              <span className="text-gray-600 text-xs ml-1">{drug.contraindications.join(', ')}</span>
                            </div>
                          )}
                          {drug.sideEffects && drug.sideEffects.length > 0 && (
                            <div>
                              <span className="font-medium text-orange-600">Side Effects:</span>
                              <span className="text-gray-600 text-xs ml-1">{drug.sideEffects.join(', ')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Non-Pharmacological */}
              {condition.management.nonPharmacological && condition.management.nonPharmacological.length > 0 && (
                <div className="mb-6">
                  <h3 className="subsection-title">Non-Pharmacological Management</h3>
                  <ul className="list-disc list-inside space-y-2">
                    {condition.management.nonPharmacological.map((item, i) => (
                      <li key={i} className="text-gray-700 text-content">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Surgical */}
              {condition.management.surgical && condition.management.surgical.length > 0 && (
                <div className="mb-6">
                  <h3 className="subsection-title">Surgical Interventions</h3>
                  <ul className="list-disc list-inside space-y-2">
                    {condition.management.surgical.map((item, i) => (
                      <li key={i} className="text-gray-700 text-content">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Monitoring */}
              {condition.management.monitoring && condition.management.monitoring.length > 0 && (
                <div className="mb-6">
                  <h3 className="subsection-title">
                    <Activity className="w-4 h-4 inline mr-1" /> Monitoring
                  </h3>
                  <ul className="list-disc list-inside space-y-2">
                    {condition.management.monitoring.map((item, i) => (
                      <li key={i} className="text-gray-700 text-content">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Follow-up */}
              {condition.management.followUp && (
                <div className="mb-6">
                  <h3 className="subsection-title">Follow-Up</h3>
                  {renderFormattedText(condition.management.followUp)}
                </div>
              )}
              
              {/* Referral Criteria */}
              {condition.management.referralCriteria && condition.management.referralCriteria.length > 0 && (
                <div>
                  <h3 className="subsection-title">{t('referralCriteria')}</h3>
                  <ul className="list-disc list-inside space-y-2">
                    {condition.management.referralCriteria.map((criteria, i) => (
                      <li key={i} className="text-gray-700 text-content">{criteria}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* Prognosis Section */}
            {condition.prognosis && (
              <section className="content-card">
                <h2 className="section-title">
                  <Heart className="w-5 h-5 text-blue-600 flex-shrink-0" /> Prognosis
                </h2>
                {renderFormattedText(condition.prognosis)}
              </section>
            )}

            {/* Prevention Section */}
            {condition.prevention && (
              <section className="content-card">
                <h2 className="section-title">
                  <Shield className="w-5 h-5 text-blue-600 flex-shrink-0" /> Prevention
                </h2>
                {renderFormattedText(condition.prevention)}
              </section>
            )}

            {/* Patient Education Section */}
            {condition.patientEducation && (
              <section className="patient-education-card">
                <h2 className="section-title text-green-800">
                  <BookOpen className="w-5 h-5 flex-shrink-0" /> Patient Education
                </h2>
                {renderFormattedText(condition.patientEducation)}
              </section>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="condition-sidebar space-y-6">
            
            {/* Red Flags */}
            {condition.redFlags && condition.redFlags.length > 0 && (
              <section className="red-flags-card">
                <h2 className="text-lg font-bold text-red-700 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" /> {t('redFlags')}
                </h2>
                <ul className="space-y-2">
                  {condition.redFlags.map((flag, i) => (
                    <li key={i} className="text-red-700 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{flag}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Quick Reference */}
            <section className="content-card">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{t('quickReference')}</h2>
              <dl className="space-y-3">
                <div><dt className="text-sm text-gray-500">{t('specialty')}</dt><dd className="font-medium text-gray-900">{condition.specialty}</dd></div>
                <div><dt className="text-sm text-gray-500">{t('bodySystem')}</dt><dd className="font-medium text-gray-900">{condition.bodySystem}</dd></div>
                <div><dt className="text-sm text-gray-500">{t('icdCode')}</dt><dd className="font-medium text-gray-900">{condition.icdCode}</dd></div>
                <div>
                  <dt className="text-sm text-gray-500">{t('resourceLevel')}</dt>
                  <dd className={`inline-block px-2 py-1 text-sm font-semibold rounded mt-1 ${
                    condition.resourceLevel === 'BASIC' ? 'bg-green-100 text-green-700' :
                    condition.resourceLevel === 'INTERMEDIATE' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                  }`}>{condition.resourceLevel}</dd>
                </div>
                {condition.evidenceGrade && (<div><dt className="text-sm text-gray-500">Evidence Grade</dt><dd className="font-medium text-gray-900">Grade {condition.evidenceGrade}</dd></div>)}
                <div><dt className="text-sm text-gray-500">Last Updated</dt><dd className="font-medium text-gray-900">{condition.lastUpdated}</dd></div>
              </dl>
            </section>

            {/* Differential Diagnosis */}
            {condition.differentialDiagnosis && condition.differentialDiagnosis.length > 0 && (
              <section className="content-card">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Differential Diagnosis</h2>
                <ul className="space-y-2">
                  {condition.differentialDiagnosis.map((dx, i) => (
                    <li key={i} className="text-gray-700 text-sm">• {dx}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Complications */}
            {condition.complications && condition.complications.length > 0 && (
              <section className="content-card">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Complications</h2>
                <ul className="space-y-2">
                  {condition.complications.map((comp, i) => (
                    <li key={i} className="text-gray-700 text-sm">• {comp}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* References - ALWAYS VISIBLE */}
            {condition.references && condition.references.length > 0 && (
              <section className="references-card">
                <h2 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 flex-shrink-0" /> References
                </h2>
                <ol className="space-y-3 list-decimal list-inside">
                  {condition.references.map((ref, i) => (
                    <li key={i} className="text-blue-900 text-sm leading-relaxed">{ref}</li>
                  ))}
                </ol>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
